/**
 * chat v1 HTTP API — a Cognito-JWT-bearer fetch helper and the request functions over it.
 * Modeled on `bffJson` (relay.ts) but with `Authorization: Bearer` + a distinct base URL
 * instead of the same-origin session cookie. Shapes here are the LIVE backend's, verified
 * against the deployed stack (CHAT-SERVICE.md §5); do not assume beyond what was probed.
 */
import { CHATV1 } from './config'
import { chatv1Token } from './token'
import { chatv1DeviceId } from './deviceId'
import { refreshChatv1Token, tokenStale } from './auth'

/** A non-2xx from the API, carrying the backend's `error` code (e.g. `not_friends`,
 *  `unknown_peer`, `device_not_registered`, `invalid_device_id`). */
export class Chatv1Error extends Error {
  constructor(
    public status: number,
    public code: string,
  ) {
    super(`chatv1 ${status} ${code}`)
    this.name = 'Chatv1Error'
  }
}

/**
 * How long any chat-v1 HTTP call may hang before it is called a failure.
 *
 * Generous on purpose — these are small JSON calls, but one of them mints an
 * upload slot on a phone that may be on a weak connection, and a deadline that
 * fires while the request was merely slow turns a working send into a spurious
 * error. Well under the patience of someone staring at a send button.
 */
const CHATV1_HTTP_TIMEOUT_MS = 20_000

async function bearer<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  // The id_token lives only in memory (token.ts) and is short-lived, so on a
  // fresh load — or after it expires — chatv1Token() can be null exactly when a
  // query fires. Rather than fail the call, mint one from the stored refresh
  // token first. This is why a returning account (no login redirect this visit)
  // could still see "couldn't load projects": the query raced the token.
  //
  // STALE counts as missing (avg-main's diagnosis, curl-verified): the gateway
  // answers an expired-but-present token with 403 — not 401 — so sending one and
  // hoping is a red "http_403" the old 401-only retry never caught. Renew BEFORE
  // asking whenever expiry is within tokenStale()'s window; if the renewal
  // fails, fall back to the token we still hold — the 401/403 retry below is
  // the net for a wrong guess either way.
  let jwt = chatv1Token()
  if (!jwt || tokenStale()) jwt = (await refreshChatv1Token()) ?? jwt
  if (!jwt) throw new Chatv1Error(401, 'no_token')
  const res = await fetch(CHATV1.http + path, {
    ...init,
    cache: 'no-store',
    /*
     * A REQUEST THAT NEVER SETTLES IS WORSE THAN ONE THAT FAILS.
     *
     * Owner, 2026-09-18: 有時候圖+文的送出完全會被卡住 — image+text sends
     * occasionally stuck forever, with the picture and the caption still sitting
     * in the composer. Every one of those awaits ends up here or at the S3 POST
     * beside it, and neither carried a deadline: on a stalled mobile connection
     * `fetch` does not reject, it simply never resolves. The send's `finally`
     * then never runs, `sending` stays true, and the send button — which is
     * `disabled={sending || …}` — is dead for the rest of the session.
     *
     * The bug is not that the network failed; it is that nothing here ever
     * decided it had. A deadline turns an invisible hang into an ordinary error
     * the composer already knows how to show and the person can retry.
     *
     * `init.signal` wins when a caller brought its own, so this is a floor and
     * not a ceiling.
     */
    signal: init.signal ?? AbortSignal.timeout(CHATV1_HTTP_TIMEOUT_MS),
    headers: {
      Authorization: `Bearer ${jwt}`,
      ...(init.body != null ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  })
  // Token went bad mid-flight: renew silently from the refresh token and retry once.
  // 403 as well as 401 — the gateway's word for "expired/invalid token" is 403
  // (clock skew and mid-request expiry land here too). An APP-level 403
  // (not_a_member …) retries once harmlessly and then throws with the backend's
  // own error code, exactly as before.
  if ((res.status === 401 || res.status === 403) && !retried && (await refreshChatv1Token())) {
    return bearer<T>(path, init, true)
  }
  const text = await res.text()
  let data: unknown = null
  try {
    data = text ? JSON.parse(text) : null
  } catch {
    /* non-JSON body — leave data null and let the status decide */
  }
  if (!res.ok) {
    const code =
      data && typeof data === 'object' && 'error' in data
        ? String((data as { error: unknown }).error)
        : `http_${res.status}`
    throw new Chatv1Error(res.status, code)
  }
  return data as T
}

// ── shapes (all verified against the live backend) ─────────────────────────────

/** The other party of a DM, resolved by the server via the BFF PROFILE# bridge. */
export type ConvPeer = {
  key: string
  email: string
  name?: string | null
  picture?: string | null
}

/** One row of `GET /conversations?device=`. `title` is the project name; DMs carry `peer`. */
export type ConversationRow = {
  conv_id: string
  kind: 'dm' | 'project'
  security_mode: 'managed' | 'e2e-required'
  owner?: string
  title?: string
  /** a group's chat room: the group it follows (chat internalGroup.ts) */
  group_id?: string
  /** the other person, DM only (absent on DMs created before the enrichment) */
  peer?: ConvPeer
  /** GSI1 sort key — a zero-padded string, newest-largest. */
  last_activity?: string
  role?: 'owner' | 'member'
  status?: 'active' | 'left' | 'archived'
  /** newest seq in the conversation */
  last_seq: number
  /** this human's read cursor */
  last_read_seq: number
  /** messages after last_read_seq not sent by me, bounded (server caps at 99+) */
  unread?: number
  /** THIS device's delivery cursor (server-authoritative); reconcile when < last_seq */
  my_ack_seq?: number
  // ── card extras: counts are project-only; the preview strip rides project AND dm
  //    rows (the friend cards draw the same strip — owner, 2026-08-27) ──
  /** active human members. */
  member_count?: number
  /** attached agents. */
  agent_count?: number
  /** last message, one line (a file card shows "📎 name" / its caption, not raw JSON). */
  last_msg_preview?: string
  /** the last speaker's display name (agent name, or the human's profile name). */
  last_from?: string
  /** the last message's agent hash when an agent spoke it (drives the preview avatar);
   *  absent ⇒ a human wrote it. */
  last_from_hash?: string
  /** A HUMAN last speaker's address and photo (absent for an agent) — the card draws
   *  their face from these instead of the neutral dot (owner, 2026-09-27). */
  last_from_email?: string
  last_from_picture?: string
}

/** Created-conversation META (POST /conversations). */
export type ConversationMeta = {
  conv_id: string
  kind: 'dm' | 'project'
  security_mode: 'managed' | 'e2e-required'
  creator?: string
  owner?: string
  created_at?: number
  active_member_count?: number
  title?: string
}

/** The versioned message envelope (CHAT-SERVICE.md §3). v1 writes `managed`. */
export type Envelope = {
  v: number
  mode: 'managed' | 'e2e'
  content_type: string
  body?: string
  /** opaque inner envelope for v2/e2e (unused in v1) */
  envelope?: unknown
  attachments?: { s3_key: string; size: number; content_type: string }[]
}

/** A stored message, as it arrives in a `message`/`backfill` frame or a history page.
 *  `sender_name`/`sender_picture` are server-enriched from the sender's profile (absent for
 *  senders without one, e.g. the dev admin account). */
export type WireMessage = {
  conv_id: string
  seq: number
  ts: number
  msg_id: string
  sender: string
  sender_name?: string | null
  sender_picture?: string | null
  envelope: Envelope
  /** Set when an AGENT spoke (owner's key is `sender`, but it speaks with its own
   *  identity): the agent's channel hash, and its display name (server-enriched from
   *  the roster on both the live frame and history). */
  via_agent?: string
  via_agent_name?: string | null
  /** A reply (chat messages.ts ReplyFields; owner, 2026-10-20): the top-level message it hangs under, who wrote that
   *  one, and a short piece of it. */
  reply_to?: number
  reply_to_sender?: string
  reply_to_text?: string
  /** Sent from a whiteboard's chat (chat messages.ts BoardFields; owner, 2026-10-30): the board's id, and its name
   *  when the message was sent. */
  board?: string
  board_name?: string
  /** About an AREA of that board (chat messages.ts AreaFields; owner, 2026-10-31): the box, the items seen in it, and
   *  its picture's key (board/area.ts messageArea reads it). */
  area?: Record<string, unknown>
  /** Sent from a page's chat (chat messages.ts PageFields; owner, 2026-11-02): the page's id, and its name then. */
  page?: string
  page_name?: string
  /** The top-level message was an agent's: its hash and name (its words are the agent's, not its owner's). */
  reply_to_agent?: string
  reply_to_agent_name?: string
}

// ── requests ───────────────────────────────────────────────────────────────────

/** Register this browser's device — REQUIRED before `getTicket` (§4.0). Idempotent. */
export const registerDevice = () =>
  bearer<{ ok: boolean; device: string }>('/devices', {
    method: 'POST',
    body: JSON.stringify({ device: chatv1DeviceId(), platform: 'web' }),
  })

/** A single-use 60s WebSocket ticket. Mint one immediately before each connect. */
export const getTicket = () =>
  bearer<{ ticket: string; expires_at: number }>('/ws-ticket', {
    method: 'POST',
    body: JSON.stringify({ device: chatv1DeviceId() }),
  })

/** Every conversation this user is in, each with this device's cursor — the batch
 *  reconcile entry: backfill only rows where `my_ack_seq < last_seq`. `kind` narrows it
 *  server-side (avg-main added `?kind=project`) so the project list needn't drag DMs back. */
export const listConversations = (kind?: 'dm' | 'project') => {
  const q = new URLSearchParams({ device: chatv1DeviceId() })
  if (kind) q.set('kind', kind)
  return bearer<{ conversations: ConversationRow[] }>(`/conversations?${q.toString()}`)
}

/** Open (or resolve the existing) conversation. DM derives its id from the pair and needs a
 *  friend `peer` (email or user id); project mints a fresh id and takes an optional `title`. */
export const createConversation = (input: {
  kind: 'dm' | 'project'
  peer?: string
  /** Which friend, when `peer`'s address is two of them — the friend list's `account` handle. */
  peer_account?: string
  title?: string
}) =>
  bearer<{ conversation: ConversationMeta; existed: boolean }>('/conversations', {
    method: 'POST',
    body: JSON.stringify({ security_mode: 'managed', ...input }),
  })

/** A page of history, older-than-`beforeSeq` (omit for the latest page). */
export const getMessages = (convId: string, beforeSeq?: number, limit = 50) => {
  const q = new URLSearchParams({ limit: String(limit) })
  if (beforeSeq != null) q.set('before_seq', String(beforeSeq))
  return bearer<{ messages: WireMessage[] }>(
    `/conversations/${encodeURIComponent(convId)}/messages?${q.toString()}`,
  )
}

/** One reaction — an emoji one person put on one message (owner, 2026-10-17: Slack's reactions). */
export type ReactionRow = { seq: number; emoji: string; user: string; ts: number }

/** Every reaction on the messages in [fromSeq, toSeq] (the service caps a range at 500 messages). */
export const getReactions = (convId: string, fromSeq: number, toSeq: number) =>
  bearer<{ reactions: ReactionRow[]; truncated?: boolean }>(
    `/conversations/${encodeURIComponent(convId)}/reactions?from_seq=${fromSeq}&to_seq=${toSeq}`,
  )

/** Put one emoji on a message, or take it back. `changed` is false when it already was so. */
export const setReaction = (convId: string, seq: number, emoji: string, on: boolean) =>
  bearer<{ ok: boolean; changed: boolean }>(`/conversations/${encodeURIComponent(convId)}/reactions`, {
    method: 'POST',
    body: JSON.stringify({ seq, emoji, on }),
  })

/**
 * A message one person kept in a project (owner, 2026-11-01: 這個書籤就只有該使用者自己可以看到): which message, when it was
 * kept (ms), and the message as it now is — `null` when it is gone. The service answers each person their own alone.
 */
export type RoomBookmarkRow = { seq: number; ts: number; message: WireMessage | null }

/** This person's bookmarks in one room, the most recently kept first. */
export const getRoomBookmarks = (convId: string) =>
  bearer<{ bookmarks: RoomBookmarkRow[] }>(`/conversations/${encodeURIComponent(convId)}/bookmarks`)

/** Keep a message, or let it go. `changed` is false when it already was so. */
export const setRoomBookmark = (convId: string, seq: number, on: boolean) =>
  bearer<{ ok: boolean; changed: boolean }>(`/conversations/${encodeURIComponent(convId)}/bookmarks`, {
    method: 'POST',
    body: JSON.stringify({ seq, on }),
  })

/**
 * One of the room's whiteboards (owner, 2026-10-30: 新增不同的 board … 可以更改名稱). `main` is every room's first.
 * `by`/`via` made it; `last_by`/`last_via`/`last_ts` wrote to it last; `seen` is how far THIS person has seen it
 * (the list answers it to them alone; absent where they never have).
 */
export type BoardInfo = {
  board: string
  name: string
  created_ts: number
  rev: number
  by?: string
  via?: string
  last_by?: string
  last_via?: string
  last_ts?: number
  seen?: number
  /** Its lock (board/EditLock): absent, everyone in the room may change it. */
  edit?: 'owner' | 'some'
  editors?: string[]
}

/** Who may change a board or a page: everyone in the room, its owner alone, or its owner and the people chosen. */
export type EditMode = 'all' | 'owner' | 'some'

/** The room's whiteboards: `main` first, then in the order they were made (chat lambda/boards.ts). */
export const listBoards = (convId: string) =>
  bearer<{ boards: BoardInfo[] }>(`/conversations/${encodeURIComponent(convId)}/boards`)

/** A new board, by name — the service mints its id. */
export const createBoard = (convId: string, name: string) =>
  bearer<{ board: BoardInfo }>(`/conversations/${encodeURIComponent(convId)}/boards`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  })

/** A board's new name. */
export const renameBoard = (convId: string, board: string, name: string) =>
  bearer<{ board: BoardInfo }>(`/conversations/${encodeURIComponent(convId)}/boards/${encodeURIComponent(board)}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  })

/** Who may change a board (its owner's to say, or the room owner's). */
export const setBoardLock = (convId: string, board: string, edit: EditMode, editors?: string[]) =>
  bearer<{ board: BoardInfo }>(`/conversations/${encodeURIComponent(convId)}/boards/${encodeURIComponent(board)}`, {
    method: 'PATCH',
    body: JSON.stringify({ edit, ...(editors ? { editors } : {}) }),
  })

/** This person has seen a board up to `rev` (owner, 2026-10-30: the Whiteboard button's red dot). Only ever up. */
export const markBoardSeen = (convId: string, board: string, rev: number) =>
  bearer<{ ok: boolean; board: string; rev: number }>(
    `/conversations/${encodeURIComponent(convId)}/boards/${encodeURIComponent(board)}/seen`,
    { method: 'POST', body: JSON.stringify({ rev }) },
  )

/** One whiteboard (owner, 2026-10-29: 跟 Miro 一樣的白板) — every live element on it. */
export const getBoard = (convId: string, board: string) =>
  bearer<{ board: string; name: string; rev: number; elements: import('../board/model').El[]; truncated?: boolean }>(
    `/conversations/${encodeURIComponent(convId)}/boards/${encodeURIComponent(board)}`,
  )

/** Write to a whiteboard. The answer holds every element the write touched, as the service now has it. */
export const postBoardOps = (convId: string, board: string, ops: import('../board/model').Op[], client: string) =>
  bearer<{
    ok: boolean
    board: string
    rev: number
    elements: import('../board/model').El[]
    refused?: Array<{ id: string; error: string }>
  }>(`/conversations/${encodeURIComponent(convId)}/boards/${encodeURIComponent(board)}/ops`, {
    method: 'POST',
    body: JSON.stringify({ ops, client }),
  })

/** Claim an image this person uploaded (mintUpload → S3) for a board: everyone in the room may read it after. */
export const claimBoardImage = (convId: string, board: string, s3Key: string) =>
  bearer<{ ok: boolean; s3_key: string }>(
    `/conversations/${encodeURIComponent(convId)}/boards/${encodeURIComponent(board)}/images`,
    { method: 'POST', body: JSON.stringify({ s3_key: s3Key }) },
  )

/**
 * A room's page (owner, 2026-11-02: 把 Notion 這個頁面編輯的功能照抄過來 … 之後我們可以有自己的頁面功能) — chat
 * lambda/pages.ts, docs/CHAT-SERVICE.md §5. Like a board's list row; a page exists only once made.
 */
export type PageInfo = {
  page: string
  name: string
  created_ts: number
  rev: number
  by?: string
  via?: string
  last_by?: string
  last_via?: string
  last_ts?: number
  seen?: number
  /** Its lock (board/EditLock): absent, everyone in the room may change it. */
  edit?: 'owner' | 'some'
  editors?: string[]
}

const pagesPath = (convId: string, page?: string) =>
  `/conversations/${encodeURIComponent(convId)}/pages${page ? `/${encodeURIComponent(page)}` : ''}`

/** The room's pages, oldest first. */
export const listPages = (convId: string) => bearer<{ pages: PageInfo[] }>(pagesPath(convId))

/** A new page, by name — the service mints its id. */
export const createPage = (convId: string, name: string) =>
  bearer<{ page: PageInfo }>(pagesPath(convId), { method: 'POST', body: JSON.stringify({ name }) })

export const renamePage = (convId: string, page: string, name: string) =>
  bearer<{ page: PageInfo }>(pagesPath(convId, page), { method: 'PATCH', body: JSON.stringify({ name }) })

/** Who may change a page (its owner's to say, or the room owner's). */
export const setPageLock = (convId: string, page: string, edit: EditMode, editors?: string[]) =>
  bearer<{ page: PageInfo }>(pagesPath(convId, page), { method: 'PATCH', body: JSON.stringify({ edit, ...(editors ? { editors } : {}) }) })

export const deletePage = (convId: string, page: string) => bearer<{ ok: boolean }>(pagesPath(convId, page), { method: 'DELETE' })

/** One page — every live block on it. */
export const getPage = (convId: string, page: string) =>
  bearer<{ page: string; name: string; rev: number; blocks: import('../page/pageModel').PageRow[]; truncated?: boolean }>(
    pagesPath(convId, page),
  )

/** Write to a page. The answer holds every block the write touched, as the service now has it. */
export const postPageOps = (convId: string, page: string, ops: import('../page/pageModel').PageOp[], client: string) =>
  bearer<{
    ok: boolean
    page: string
    rev: number
    blocks: import('../page/pageModel').PageRow[]
    refused?: Array<{ id: string; error: string }>
  }>(`${pagesPath(convId, page)}/ops`, { method: 'POST', body: JSON.stringify({ ops, client }) })

/** One row of `GET /conversations/{id}/members` — a human member, identity resolved by the
 *  server via the BFF PROFILE# bridge. Agents live on the separate `/agents` route. */
export type MemberRow = {
  user: string
  /** The friend list's `account` for this person (the same hash), so a roster can be matched per account. */
  account?: string
  email?: string
  name?: string | null
  picture?: string | null
  role: 'owner' | 'member'
  status: 'active' | 'left'
  joined_order: number
  joined_at?: number
}

/** The human members of a conversation (members-only readable). */
export const getMembers = (convId: string) =>
  bearer<{ members: MemberRow[] }>(`/conversations/${encodeURIComponent(convId)}/members`)

/** Add a human — the add-human action. `member` is an email (server EMAILIDX-resolves it)
 *  or an account key; inviting a friend passes their email, and their `account` handle when
 *  the friend list has one (which of two friends at one address — the server cannot guess). */
export const addMember = (convId: string, member: string, memberAccount?: string) =>
  bearer<{ ok: boolean; joined_order?: number }>(
    `/conversations/${encodeURIComponent(convId)}/members`,
    {
      method: 'POST',
      body: JSON.stringify(memberAccount ? { member, member_account: memberAccount } : { member }),
    },
  )

/** Leave a conversation — DELETE my own membership. `successor` transfers ownership when
 *  the departing member is the owner (the server dictates when that is required). */
export const leaveConversation = (convId: string, successor?: string) => {
  const q = successor ? `?successor=${encodeURIComponent(successor)}` : ''
  return bearer<{ ok: boolean; archived?: boolean; owner?: string }>(
    `/conversations/${encodeURIComponent(convId)}/members/me${q}`,
    { method: 'DELETE' },
  )
}

// ── agents (甲 lane) ─────────────────────────────────────────────────────────────

/** One AGENT# roster row. `agent_hash` is the edge's channel hash (the pane identity the
 *  message feed routes back to); `device_id` is the hosting machine; `owner_user` is who
 *  attached it (only they may detach / set mode). */
export type AgentRosterRow = {
  agent_hash: string
  owner_user: string
  device_id: string
  name: string
  mode: 'speak' | 'listen' | 'mute'
}

/** The conversation's attached agents (members-only readable). */
export const listAgents = (convId: string) =>
  bearer<{ agents: AgentRosterRow[] }>(`/conversations/${encodeURIComponent(convId)}/agents`)

/** Attach one of MY agents — `agentHash` is its channel hash, `deviceId` the machine it
 *  runs on (so the feed can push the room's messages to its pane). Own-agents only. */
export const addAgent = (convId: string, agentHash: string, deviceId: string, name: string) =>
  bearer<{ ok: boolean }>(`/conversations/${encodeURIComponent(convId)}/agents`, {
    method: 'POST',
    body: JSON.stringify({ agent_hash: agentHash, device_id: deviceId, name }),
  })

/** A group's posting limit as it stands for me in this room (chat/lambda/quota.ts). `limit` null = none. */
export type ConvQuota = {
  limit: number | null
  used: number
  extra: number
  resets_at: number
  exempt: boolean
  agent_limit: number | null
}
export const getConvQuota = (convId: string) =>
  bearer<ConvQuota>(`/conversations/${encodeURIComponent(convId)}/quota`)

/** Detach an agent (any member may remove one). */
export const removeAgent = (convId: string, agentHash: string) =>
  bearer<{ ok: boolean }>(
    `/conversations/${encodeURIComponent(convId)}/agents/${encodeURIComponent(agentHash)}`,
    { method: 'DELETE' },
  )

/** Owner-only dial: speak / listen / mute. */
export const setAgentModeReq = (convId: string, agentHash: string, mode: string) =>
  bearer<{ ok: boolean }>(
    `/conversations/${encodeURIComponent(convId)}/agents/${encodeURIComponent(agentHash)}`,
    { method: 'PATCH', body: JSON.stringify({ mode }) },
  )

// ── attachments (S3, managed mode) ──────────────────────────────────────────────

/** Presigned upload: the browser POSTs `post.fields` + the file (last) to `post.url`,
 *  then references `s3_key` in a message's envelope.attachments to claim it. */
export type UploadMint = { s3_key: string; post: { url: string; fields: Record<string, string> } }

/** Mint an upload — presigned S3 POST for a `size`/`content_type` in this conversation. */
export const mintUpload = (convId: string, size: number, contentType: string) =>
  bearer<UploadMint>('/attachments', {
    method: 'POST',
    body: JSON.stringify({ conv_id: convId, size, content_type: contentType }),
  })

/** Presign a download for a CLAIMED attachment (404 `not_available` until referenced).
 *  `s3Key` is `uploads/<conv>/<uuid>` — already path-safe, sent as the greedy {key+}. */
export const attachmentUrl = (s3Key: string) => bearer<{ url: string }>(`/attachments/${s3Key}`)

// ── games (chat lambda/games.ts) ────────────────────────────────────────────────
// Owner, 2026-10-19: 西洋棋, 讓兩個使用者可以互相對弈 … 也可以跟他自己的 Agent 來配對來下棋; AI 輔助 the opponent is
// told about. The service is the referee; every answer is the game as THIS person sees it — seats by name and
// picture, never an account.

export type GameSide = 'w' | 'b'
/** The games there are (owner, 2026-10-19: 西洋棋, then 五子棋, then xiangqi, shogi, mahjong — one at a time). */
export type GameType = 'chess' | 'gomoku' | 'xiangqi'
export type GameSeat =
  | { kind: 'user'; name: string; picture: string | null; you: boolean; assist: boolean }
  | { kind: 'agent'; name: string; yours: boolean; assist: boolean }
export type GameView = {
  id: string
  type: GameType
  /** match: two people paired; friend: one invited the other; agent: you against your own agent. */
  mode: 'match' | 'friend' | 'agent'
  you: GameSide | null
  players: Record<GameSide, GameSeat>
  moves: string[]
  /** The moves as a reader writes them (xiangqi's WXF "C2.5" for a stored "h2e2"); absent from an older service. */
  notation?: string[]
  /** Where your pieces may go, on your move, from a game whose rules the app has no copy of (xiangqi); else null. */
  legal?: string[] | null
  fen: string
  turn: GameSide
  last: { from: string; to: string } | null
  check: boolean
  /** The row that won it (gomoku's five), for the board to mark. */
  line: string[] | null
  /** invited: a friend has yet to answer — nothing is played until they accept. */
  status: 'invited' | 'active' | 'over'
  result: '1-0' | '0-1' | '1/2-1/2' | null
  reason:
    | 'checkmate'
    | 'stalemate'
    | 'repetition'
    | 'fifty_moves'
    | 'insufficient'
    | 'five'
    | 'board_full'
    | 'perpetual_check'
    | 'sixty_moves'
    | 'move_limit'
    | 'resigned'
    | 'abandoned'
    | 'declined'
    | 'cancelled'
    | null
  /** Who sent the invitation of a friend game. */
  invited: 'you' | 'them' | null
  /** What the two players said to each other, oldest first. */
  chat: { from: GameSide; text: string; at: number }[]
  /** Your agent's latest hint, for this position only. */
  hint: { san: string; from: string; to: string; note: string } | null
  hint_pending: boolean
  advisor: { name: string } | null
  agent_thinking: boolean
  agent_problem: 'offline' | 'unreachable' | 'illegal' | null
  v: number
  updated_at: number
}
/** One of your agents, picked to play or to advise. */
export type GameAgentPick = { agent_hash: string; device_id: string; name: string }

const gamePath = (id: string, verb = '') => `/games/${encodeURIComponent(id)}${verb ? `/${verb}` : ''}`

/** Your games — the two-sided ones under `games`, mahjong tables under `mahjong` (absent from an older service). */
export const listGames = () => bearer<{ games: GameView[]; mahjong?: MahjongView[] }>('/games')
export const getGame = (id: string) => bearer<{ game: GameView }>(gamePath(id))
/** Play one of your own agents; `side` is the colour YOU take (random when omitted). */
export const startAgentGame = (type: GameType, agent: GameAgentPick, side?: GameSide) =>
  bearer<{ game: GameView }>('/games', {
    method: 'POST',
    body: JSON.stringify({ type, vs: 'agent', ...agent, ...(side ? { side } : {}) }),
  })
/** Find a player of this game: paired at once with someone waiting, or put up to wait (told by a frame when paired). */
export const findGameMatch = (type: GameType) =>
  bearer<{ status: 'waiting' } | { status: 'matched'; game: GameView }>('/games/match', {
    method: 'POST',
    body: JSON.stringify({ type }),
  })
export const stopGameMatch = (type: GameType) =>
  bearer<{ ok: boolean }>(`/games/match?type=${encodeURIComponent(type)}`, { method: 'DELETE' })
/** A piece game moves from a square to a square; a stone game places on one (`to` alone). */
export const playGameMove = (id: string, move: { from?: string; to: string; promotion?: string }) =>
  bearer<{ game: GameView }>(gamePath(id, 'move'), { method: 'POST', body: JSON.stringify(move) })
export const resignGame = (id: string) => bearer<{ game: GameView }>(gamePath(id, 'resign'), { method: 'POST' })
/** AI assist: ask one of your agents for a hint — your opponent is shown that you use assist from now on. */
export const askGameHint = (id: string, agent: GameAgentPick) =>
  bearer<{ game: GameView }>(gamePath(id, 'hint'), { method: 'POST', body: JSON.stringify(agent) })
/** Ask the agent you play against again (it went quiet, was offline, or did not answer with a move). */
export const nudgeGameAgent = (id: string) => bearer<{ game: GameView }>(gamePath(id, 'nudge'), { method: 'POST' })
/** Invite a FRIEND (by the address you have for them); `side` is the colour you take (random when omitted).
 *  `account` — the friend list's handle — says which friend when the address is two of them. */
export const inviteFriendGame = (type: GameType, email: string, side?: GameSide, account?: string) =>
  bearer<{ game: GameView }>('/games/invite', {
    method: 'POST',
    body: JSON.stringify({ type, email, ...(side ? { side } : {}), ...(account ? { account } : {}) }),
  })
export const acceptGame = (id: string) => bearer<{ game: GameView }>(gamePath(id, 'accept'), { method: 'POST' })
export const declineGame = (id: string) => bearer<{ game: GameView }>(gamePath(id, 'decline'), { method: 'POST' })
/** Say something to the other player — typed, or spoken and written down by the app's voice input. */
export const sayInGame = (id: string, text: string) =>
  bearer<{ game: GameView }>(gamePath(id, 'say'), { method: 'POST', body: JSON.stringify({ text }) })

// ── mahjong (chat lambda/mahjongTable.ts) ───────────────────────────────────────────────────────────────────────
// Owner, 2026-10-26: 開始做麻將; 邀請者可以預設籌碼. Taiwan sixteen-tile at a table of four: people, your agents and
// the computer. Tiles are numbers 0…143 (kind = tile >> 2; 136…143 the flowers — games/mahjongTiles.ts). Every view
// is cut for its viewer: your own tiles, everyone's sets, flowers and discards, and the other hands only once a deal
// is over. The service answers a table under `mahjong`, never `game`.

export type MahjongSettings = { chips: number; base: number; per_tai: number; rounds: 1 | 4 }
export type MahjongWind = 'E' | 'S' | 'W' | 'N'
export type MahjongMeld = { type: 'chow' | 'pung' | 'kong' | 'hidden_kong'; tiles: number[]; from: number | null }
export type MahjongSeat = {
  kind: 'user' | 'agent' | 'house'
  name: string
  picture: string | null
  you: boolean
  /** One of your own agents. */
  yours: boolean
  /** Left the table: the computer plays the seat. */
  left: boolean
  /** A friend game's invitation for this seat. */
  invite: 'pending' | 'accepted' | 'declined' | null
  assist: boolean
  chips: number
  wind: MahjongWind | null
  dealer: boolean
  /** How many tiles the hand holds. */
  held: number
  /** The hand — yours, or everyone's once the deal is over; else null. */
  tiles: number[] | null
  melds: MahjongMeld[]
  flowers: number[]
  river: number[]
  /** An agent whose turn it is. */
  thinking: boolean
  agent_problem: 'offline' | 'unreachable' | 'illegal' | null
}
export type MahjongFan = { name: string; zh: string; tai: number }
export type MahjongView = {
  id: string
  type: 'mahjong'
  mode: 'match' | 'friend' | 'agent'
  /** Your seat (0–3), or null. */
  you: number | null
  status: 'invited' | 'active' | 'over'
  reason: 'finished' | 'bust' | 'abandoned' | 'declined' | 'cancelled' | null
  invited: 'you' | 'them' | null
  settings: MahjongSettings
  round: { wind: MahjongWind; hand: number; dealer: number; streak: number; wall: number }
  seats: MahjongSeat[]
  hand: { drawn: number | null } | null
  phase: {
    t: 'turn' | 'claim' | 'done'
    seq: number
    /** Whose turn, or who discarded the tile on offer. */
    seat: number | null
    tile: number | null
    rob: boolean
    /** When the decision awaited times out (epoch seconds). */
    deadline: number | null
    /** The table waits on you: your turn, your claim, or your "next deal". */
    yours: boolean
  } | null
  options: {
    discard: boolean
    win: boolean
    kongs: number[]
    claim: { win: boolean; pung: boolean; kong: boolean; chows: Array<[number, number]> } | null
  } | null
  /** Your answer to the claim on offer, while the others decide. */
  answered: string | null
  log: Array<{ seat: number; act: string; tile: number | null }>
  /** The deal just over. */
  result: {
    kind: 'win' | 'draw'
    win: { seat: number; from: number | null; tile: number | null; items: MahjongFan[]; tai: number; dealer_tai: number } | null
    deltas: number[]
    ready: boolean[]
  } | null
  history: Array<{ hand: number; wind: number; dealer: number; winner: number | null; from: number | null; tai: number; deltas: number[] }>
  chat: Array<{ from: number; text: string; at: number }>
  hint: { act: string; tile: number | null; with: [number, number] | null; note: string } | null
  hint_pending: boolean
  advisor: { name: string } | null
  v: number
  updated_at: number
}

/** A game opened by its id alone: a board's or a table's. */
export const getAnyGame = (id: string) => bearer<{ game?: GameView; mahjong?: MahjongView }>(gamePath(id))
export const getMahjong = async (id: string): Promise<MahjongView> => {
  const r = await getAnyGame(id)
  if (!r.mahjong) throw new Chatv1Error(404, 'no_such_game')
  return r.mahjong
}
/** A table with up to three of your agents, the computer in any seat left; your chips and stakes. */
export const startMahjong = (agents: GameAgentPick[], settings: MahjongSettings) =>
  bearer<{ mahjong: MahjongView }>('/games', { method: 'POST', body: JSON.stringify({ type: 'mahjong', agents, settings }) })
/** Up to three friends (and your agents), with your chips and stakes; they answer before play begins. */
export const inviteMahjong = (emails: string[], agents: GameAgentPick[], settings: MahjongSettings) =>
  bearer<{ mahjong: MahjongView }>('/games/invite', {
    method: 'POST',
    body: JSON.stringify({ type: 'mahjong', emails, agents, settings }),
  })
/** Sit down with whoever is looking (the computer fills the rest), or wait for someone to come. */
export const findMahjong = () =>
  bearer<{ status: 'waiting' } | { status: 'matched'; mahjong: MahjongView }>('/games/match', {
    method: 'POST',
    body: JSON.stringify({ type: 'mahjong' }),
  })
export const stopFindingMahjong = () => bearer<{ ok: boolean }>('/games/match?type=mahjong', { method: 'DELETE' })
/** A play at the table: discard, win, kong, pung, chow, pass; next (the next deal); start; tick (the time is up). */
export type MahjongPlay =
  | { action: 'discard'; tile: number; seq: number }
  | { action: 'win' | 'pung' | 'pass'; seq: number }
  | { action: 'kong'; kind?: number; seq: number }
  | { action: 'chow'; with: [number, number]; seq: number }
  | { action: 'next' | 'start' | 'tick' }
export const playMahjong = (id: string, play: MahjongPlay) =>
  bearer<{ mahjong: MahjongView }>(gamePath(id, 'act'), { method: 'POST', body: JSON.stringify(play) })
const tableVerb = (verb: string) => (id: string, body?: unknown) =>
  bearer<{ mahjong: MahjongView }>(gamePath(id, verb), { method: 'POST', ...(body ? { body: JSON.stringify(body) } : {}) })
export const leaveMahjong = tableVerb('resign')
export const acceptMahjong = tableVerb('accept')
export const declineMahjong = tableVerb('decline')
export const nudgeMahjong = tableVerb('nudge')
export const askMahjongHint = (id: string, agent: GameAgentPick) => tableVerb('hint')(id, agent)
export const sayAtMahjong = (id: string, text: string) => tableVerb('say')(id, { text })
